import React, {useEffect, useState} from 'react';
import {Header} from '../Header/Header';
import {Container} from './Container';
import {Provider, useDispatch, useSelector} from 'react-redux';
import {
    CHANGE_VIEW,
    getBannerHostPortAsync,
    getPlayerInfoAsync,
    getPlayerSettings,
    REMOVE_ALL_COUPON_EVENTS,
    SET_ACCOUNTS_LOADED,
    SET_AUTHENTICATED,
    SET_AUTHENTICATED_LOADED,
    SET_CURRENT_COUPON_TYPE,
    SET_FAVORITES_COUNT,
    SET_HAS_ACCOUNTS,
    SET_IDENTIFIED,
    SET_IDENTIFIED_LOADED,
    SET_SPORT_CHL_IN_RESULTS,
    SET_TOKEN
} from '../../store/spirs/actions';
import {parseUrl} from "../../utils/ParseQuery";
import {StoreType} from "../../store/spirs/types";
import {CouponChooseMethodSetting} from "../CouponChooseMethodSetting/CouponChooseMethod";
import {PlayerInfoMessage} from "../PlayerInfoMessage/PlayerInfoMessage"
import {changeHeaderPlayer, getFavoritesCount, getRequiredAuth, getRightPanel} from "../../spirsApi/api-client";
import {store} from "../../store";
import ErrorBoundary from "../../utils/ErrorBoundary";
import {Results} from "../Results/Results";
import {MobileCouponsModal} from "../coupons/MobileCoupons";
import {HashRouter, Route, Routes, useLocation} from "react-router-dom";
import {Menu} from "../VerticalMenu/Menu";
import {CouponView} from "../coupons/Coupons";
// import {CouponViewNavigation} from "../coupons/Coupons";
import {RightPanel} from "./RightPanel";
import {RightPanelType} from "../../models/api/codegen/code";
import {AuthContext, AuthContextType} from "../context/AuthContext";
import {Desktop} from "../adaptive/Desktop";
import "react-datepicker/dist/react-datepicker.css";
import {ReactComponent as MoneyIcon} from "../Header/money_icon.svg";
import {ReactComponent as CloseIcon} from '../icons/close.svg';
import { ToastContainer } from 'react-toastify';
import {TestOverlayProvider} from "../coupons/TestOverlayContext";
import Timeout = NodeJS.Timeout;
import { ShowBalance } from '../balance/ShowBalance';

export const langUrlRegex = "[a-z]{2}";

export const App: React.FC = () => {

    return (
        <Provider store={store}>
            <HashRouter>
                <ErrorBoundary>
                    <Routes>
                        <Route path={`/:lang(${langUrlRegex})?/*`} element={<Root/>}/>
                    </Routes>
                </ErrorBoundary>
            </HashRouter>
        </Provider>
    )
};

export const Root: React.FC = () => {
    const location = useLocation();
    const {pathname, search} = location
    const dispatch = useDispatch();
    const query = parseUrl(search === '' ? pathname : search);
    const {spirs: {playerInfo: {playerId}}} = useSelector((state: StoreType) => state);
    const [auth, setAuth] = React.useState(false);
    const [loadedAuth, setLoadedAuth] = React.useState(false);

    React.useEffect(() => {
        getRequiredAuth().then(data => {
            if (!data) return;
            setAuth(data.value);
            setLoadedAuth(true);
        })
    }, [])

    const showCLInResults = (): boolean => {
        const cl = query.cl;
        if (!cl) {
            return true;
        }
        return cl === "true";
    }

    const showSportInResults = (): boolean => {
        const sport = query.sport;
        if (!sport) {
            return true;
        }
        return sport === "true";
    }

    React.useEffect(() => {
        if (!loadedAuth || (loadedAuth && !auth)) {
            dispatch(CHANGE_VIEW("ALL"))
            return;
        }
        if (query.sessiontoken && query.sessiontoken !== '') {
            dispatch(CHANGE_VIEW("LOADING"));
        } else {
            !playerId && dispatch(CHANGE_VIEW("ONLY_RESULTS"))
        }
    }, [query.sessiontoken, auth, loadedAuth]);

    React.useEffect(() => {
        dispatch(SET_SPORT_CHL_IN_RESULTS({sport: showSportInResults(), chesslive: showCLInResults()}))
    }, [query.cl, query.sport])

    return (<RootApp token={query.sessiontoken} playerId={playerId} showLogo={true} onAuth={() => {}}/>)
};

export const RootApp: React.FC<{
    token: string | null,
    playerId: string,
    showLogo: boolean,
    onAuth: () => void,
    identified?: boolean,
    identifiedLoaded?: boolean,
    hasAccounts?: boolean,
    accountsLoaded?: boolean,
    authenticated?: boolean,
    authenticatedLoaded?: boolean
}> = (props) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {view, spirs: {playerToken}} = useSelector((state: StoreType) => state);
    const prevTokenRef = React.useRef<string | null>(playerToken);

    React.useEffect(() => {
        if (props.token) {
            dispatch(SET_TOKEN(props.token));
        }
    }, [props.token]);

    // Очистка купонов при деавторизации
    React.useEffect(() => {
        const prevToken = prevTokenRef.current;
        const currentToken = playerToken;

        if (prevToken && prevToken !== '' && prevToken !== 'GUEST' && currentToken === 'GUEST') {
            // Очищаем все три типа купонов
            dispatch(SET_CURRENT_COUPON_TYPE('EXPRESS'));
            dispatch(REMOVE_ALL_COUPON_EVENTS());
            dispatch(SET_CURRENT_COUPON_TYPE('SYSTEM'));
            dispatch(REMOVE_ALL_COUPON_EVENTS());
            dispatch(SET_CURRENT_COUPON_TYPE('ORDINAR')); // последний должен быть ОРДИНАР
            dispatch(REMOVE_ALL_COUPON_EVENTS());
        }

        prevTokenRef.current = currentToken;
    }, [playerToken, dispatch]);

    React.useEffect(() => {
        dispatch(SET_IDENTIFIED(props.identified ?? false));
    }, [props.identified]);

    React.useEffect(() => {
        dispatch(SET_IDENTIFIED_LOADED(props.identifiedLoaded ?? false));
    }, [props.identified]);

    React.useEffect(() => {
        dispatch(SET_HAS_ACCOUNTS(props.hasAccounts ?? false));
    }, [props.hasAccounts]);

    React.useEffect(() => {
        dispatch(SET_ACCOUNTS_LOADED(props.accountsLoaded ?? false));
    }, [props.accountsLoaded]);

    React.useEffect(() => {
        dispatch(SET_AUTHENTICATED(props.authenticated ?? false));
    }, [props.authenticated]);

    React.useEffect(() => {
        dispatch(SET_AUTHENTICATED_LOADED(props.authenticatedLoaded ?? false));
    }, [props.authenticatedLoaded]);

    function fetchFavoritesCount() {
        getFavoritesCount().then(res => {
            if (res) {
                let value = res.value == null ? 0 : res.value;
                dispatch(SET_FAVORITES_COUNT(value));
            }
        });
    }

    useEffect(() => {
        let id: Timeout | null;
        changeHeaderPlayer(props.playerId ? props.playerId : 'GUEST', props.token);
        if (props.playerId) {
            if (props.playerId === 'GUEST') {
                dispatch(SET_FAVORITES_COUNT(0))
            } else {
                fetchFavoritesCount();
                id = setInterval(() => {
                    fetchFavoritesCount();
                }, 5000)
            }
        }
        return () => {
            if (id) {
                clearInterval(id);
            }
        }
    }, [props.playerId, props.token]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    return (<TestOverlayProvider>
        {(view === "LOADING" || view === "ALL") && <Loading showLogo={props.showLogo} onAuth={props.onAuth}/>}
        {view === "ONLY_RESULTS" && <Results type={'ONLY_RESULTS'}/>}
        {view === "ERROR" && <ErrorView/>}
    </TestOverlayProvider>);
};

const ErrorView: React.FC = () => {
    const {spirs: {playerToken}} = useSelector((state: StoreType) => state);

    return (<div className="bg-[#272d33] text-white p-[10px]">
        Error with session: {playerToken}
    </div>)
}

const Loading: React.FC<{ showLogo: boolean, onAuth: () => void }> = ({showLogo, onAuth}) => {
    const {spirs: {playerToken}, view} = useSelector((state: StoreType) => state);

    const dispatch = useDispatch();
    useEffect(() => {
        playerToken != '' && dispatch(getPlayerInfoAsync.request({sessiontoken: playerToken}))
    }, [playerToken]);


    return (<>
        {view === 'ALL' && (
                <Main showLogo={showLogo} onAuth={onAuth}/>
        )}
    </>)
};

export const Main: React.FC<{ showLogo: boolean, onAuth: () => void }> = ({showLogo, onAuth}) => {
    const {spirs: {playerRequestCompleted, playerInfo: {playerId, showWelcome, infoMessage}, playerToken}, view} = useSelector((state: StoreType) => state);
    const balance = useSelector((state: StoreType) => state.spirs.playerInfo.balance);
    const [verticalMenuIsOpen, setVerticalMenuOpen] = useState(false);
    const [localInfoMessage, setLocalInfoMessage] = useState<string | null>(null);
    const [rightPanel, setRightPanel] = React.useState<RightPanelType | undefined>(undefined);
    const [isShow, setIsShow] = useState<boolean>(false);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch((getBannerHostPortAsync.request({})));
        getRightPanel().then(data => {
            if (!data) {
                return;
            }
            setRightPanel(data);
        })
    }, []);

    useEffect(() => {
        dispatch(getPlayerSettings.request({playerId: playerId}));
    }, [playerId]);

    const toggleVerticalMenu = React.useCallback(() => setVerticalMenuOpen(prevState => !prevState), [setVerticalMenuOpen])

    React.useEffect(() => {
        let id: Timeout | null;
        if (playerRequestCompleted && playerToken !== '' && playerToken != 'GUEST') {
            id = setTimeout(() => {
                dispatch(getPlayerInfoAsync.request({sessiontoken: playerToken}))
            }, 15000)
        }
        return () => {
            if (id) {
                clearTimeout(id);
            }
        }
    }, [playerToken, playerRequestCompleted]);

    useEffect(() => {
        if (!!infoMessage && infoMessage.length > 0) {
            setLocalInfoMessage(infoMessage);
        }
    }, [infoMessage])

    const updateMessage = (value: string) => setLocalInfoMessage(value);

    if (view !== 'ALL') {
        return <></>
    }

    const authContextProps: AuthContextType = {
        needAuth: playerToken === "GUEST",
        onAuth
    }

    const handleOpen = () => {
        setIsShow(true);
    }

    const handleClose = () => {
        setIsShow(false);
    }

    return (<>
        {view === 'ALL' && <AuthContext.Provider value={authContextProps}>
            <div>
                {showWelcome ? <CouponChooseMethodSetting/> : <></>}
                <PlayerInfoMessage updateMessage={updateMessage} message={localInfoMessage}/>
                <div className="flex justify-center bg-[#272d33] text-white">
                    <div className="flex flex-col tablet:h-screen w-full desktop2560:w-[2560px]">
                        {balance != undefined && balance > 0 && <div className="mobile:h-[31px] tablet:hidden flex justify-end items-center bg-[#171A1E] px-[10px] py-[9px] border-b-2 border-[#000]">
                            <ShowBalance/>
                        </div>}
                        <Header toggleVerticalMenu={toggleVerticalMenu} showLogo={showLogo}/>
                        <div className="tablet:min-h-screen flex flex-1 tablet:overflow-hidden">
                            <Menu toggleVerticalMenu={toggleVerticalMenu} isOpen={verticalMenuIsOpen}/>
                            <Container handleClick={handleOpen} />
                            <aside className={"w-[320px] desktop:w-[416px] bg-[#171A1E] h-[calc(100vh+68px)] max-tablet:hidden max-notebook:absolute max-notebook:z-[999] max-notebook:right-0 max-notebook:top-0" + ` max-notebook:${isShow ? 'block' : 'hidden'}`}>
                                <div className="px-[10px] py-[8px] hidden tablet:flex notebook:hidden justify-end" onClick={handleClose}>
                                    <CloseIcon />
                                </div>

                                <Desktop>
                                    {rightPanel && rightPanel.enable && <RightPanel types={rightPanel.types}/>}

                                </Desktop>
                            </aside>
                        </div>
                        {rightPanel && rightPanel.mobilePanel && <CouponView/>}
                    </div>
                </div>
                <MobileCouponsModal/>
            </div>
            <ToastContainer />
        </AuthContext.Provider>}
    </>)
};
